import { formatExpiryDates, splitByWarehouseRatio } from "src/helpers";
import { ProductCheckInColumnHeader } from "src/enums";
import { OrderProductCheckIn, TableColumn } from "src/types";
import { TextWithLineBreaks } from "src/components";
import { Table } from "../ui";

interface ProductCheckInTableProps {
  orderProductCheckIns: OrderProductCheckIn[];
}

const renderBreakdownHeader = (label: string) => (
  <div className="flex flex-col items-center gap-1">
    <span className="text-xs">{label}</span>
    <div className="flex items-center justify-center gap-3 text-[11px] font-semibold tracking-wider">
      <span className="text-violet-300">UK</span>
      <span className="text-orange-300">EU</span>
    </div>
  </div>
);

const renderBreakdownCell = (
  total: number | undefined,
  uk: number | undefined,
  eu: number | undefined,
  totalClassName: string
) => {
  const hasSplit = uk !== undefined || eu !== undefined;
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className={`font-semibold tabular-nums text-sm ${totalClassName}`}>
        {total !== undefined ? total : "-"}
      </span>
      {hasSplit && (
        <div className="inline-grid grid-cols-[1fr_1fr] gap-x-3 text-sm font-medium tabular-nums">
          <span className="text-right text-violet-600">{uk ?? "-"}</span>
          <span className="text-left text-orange-600">{eu ?? "-"}</span>
        </div>
      )}
    </div>
  );
};

const ProductCheckInTable: React.FC<ProductCheckInTableProps> = ({ orderProductCheckIns }) => {
  const compactCellWidth = { minWidth: "78px" };
  const breakdownCellStyle = { minWidth: "110px", textAlign: "center" as const };

  const showWarehouseBreakdown = orderProductCheckIns.some((row) => row.ukQty !== undefined || row.euQty !== undefined);

  const columns: TableColumn<ProductCheckInColumnHeader, OrderProductCheckIn>[] = [
    { header: ProductCheckInColumnHeader.PRODUCT_ID, column: (row) => row.productId },
    { header: ProductCheckInColumnHeader.PRODUCT_NAME, column: (row) => row.productName },
    {
      header: ProductCheckInColumnHeader.PRODUCT_SKU,
      column: (row) => (row.productSku ? row.productSku : "-"),
      styles: compactCellWidth
    },
    { header: ProductCheckInColumnHeader.PRODUCT_BARCODE, column: (row) => row.productBarcode },
    ...(showWarehouseBreakdown
      ? [
          {
            header: renderBreakdownHeader(ProductCheckInColumnHeader.RECEIVED_QTY),
            column: (row: OrderProductCheckIn) =>
              renderBreakdownCell(row.receivedQty, row.receivedUkQty, row.receivedEuQty, "text-secondary-900"),
            styles: breakdownCellStyle
          },
          {
            header: renderBreakdownHeader(ProductCheckInColumnHeader.ORDER_QTY),
            column: (row: OrderProductCheckIn) =>
              renderBreakdownCell(row.orderQty, row.ukQty, row.euQty, "text-secondary-900"),
            styles: breakdownCellStyle
          },
          {
            header: renderBreakdownHeader(ProductCheckInColumnHeader.CONFIRMED_QTY),
            column: (row: OrderProductCheckIn) => {
              const { uk: confirmedUk, eu: confirmedEu } = splitByWarehouseRatio(
                row.confirmedQty,
                row.ukQty ?? 0,
                row.euQty ?? 0
              );
              return renderBreakdownCell(row.confirmedQty, confirmedUk, confirmedEu, "text-success-700");
            },
            styles: breakdownCellStyle
          }
        ]
      : [
          {
            header: ProductCheckInColumnHeader.RECEIVED_QTY,
            column: (row: OrderProductCheckIn) => row.receivedQty?.toString(),
            styles: compactCellWidth
          },
          {
            header: ProductCheckInColumnHeader.ORDER_QTY,
            column: (row: OrderProductCheckIn) => row.orderQty?.toString(),
            styles: compactCellWidth
          },
          {
            header: ProductCheckInColumnHeader.CONFIRMED_QTY,
            column: (row: OrderProductCheckIn) => row.confirmedQty?.toString(),
            styles: compactCellWidth
          }
        ]),
    {
      header: ProductCheckInColumnHeader.EXPIRY_DATES,
      column: (row) => (row.expiryDates ? <TextWithLineBreaks text={formatExpiryDates(row.expiryDates)} /> : undefined)
    }
  ];

  return <Table<ProductCheckInColumnHeader, OrderProductCheckIn> data={orderProductCheckIns} columns={columns} />;
};

export { ProductCheckInTable };
